import {
    Button,
    CircularProgress,
    MenuItem,
    TextField,
    useTheme,
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import {
    axiosInstance,
    statesList,
    BASE_URL,
    LocalStorage,
} from '../../helpers'
import { Container } from '../Container'
import {
    Address,
    Company,
    IdentifiableNamedObject,
    Organization,
} from '../../models'
import { toast } from 'react-toastify'
import { Selector } from '../Selector'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {}

interface EntrataProperty extends Address, IdentifiableNamedObject {}

export const EntrataOnboardForm = (props: Props) => {
    const theme = useTheme()
    const [socket, setSocket] = useState<WebSocket | null>(null)

    const [organizationList, setOrganizationList] = useState<
        Organization[] | null
    >(null)

    const [getPropertiesLoading, setGetPropertiesLoading] = useState(false)
    const loadingDoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [selectedOrgId, setSelecetedOrgId] = useState(-1)
    const [
        orgForCurrentEntrataProperties,
        setOrgForCurrentEntrataProperties,
    ] = useState(-1)
    const [selectedEntrataId, setSelectedEntrataId] = useState(-1)

    const [entrataProperties, setEntrataProperties] = useState<
        EntrataProperty[]
    >([])

    const selectorStyle: React.CSSProperties = {
        flex: 1,
        marginRight: theme.spacing(1),
    }

    const buttonStyle: React.CSSProperties = { maxHeight: 50, minWidth: 125 }

    useEffect(() => {
        axiosInstance
            .get('company/organization/', { params: { entrata: true } })
            .then((res) => {
                setOrganizationList(res.data)
            })
            .catch((e) => toast.error(e))
    }, [])

    useEffect(() => {
        // Create WebSocket connection when component mounts
        const wsBaseUrl = process.env.REACT_APP_WEBSOCKET_BASE_URL
        if (!wsBaseUrl) {
            toast.error('WebSocket configuration error')
            return
        }

        const token = localStorage.getItem(LocalStorage.EZTURN4_TOKEN)
        const url = `${wsBaseUrl}integration/entrata-properties/?token=${token}`
        const ws = new WebSocket(url)

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data)

            if (Array.isArray(data)) {
                const newEntrataProperties: EntrataProperty[] = data.map(
                    (property: any) => ({
                        name: property['MarketingName'],
                        id: property['PropertyID'],
                        address1: property['Address']['Address'],
                        address2: '',
                        city: property['Address']['City'],
                        state: property['Address']['State'],
                        zip_code: property['Address']['PostalCode'],
                        country: property['Address']['Country'],
                        utc_offset: 0,
                        dst_offset: 0,
                        created_date: new Date().toISOString(),
                        modified_date: new Date().toISOString(),
                        timezone_id: '',
                        timezone_name: '',
                    }),
                )
                setEntrataProperties(newEntrataProperties)

                // Debounce: only mark done after 600ms of no further messages
                if (loadingDoneTimer.current) {
                    clearTimeout(loadingDoneTimer.current)
                }
                loadingDoneTimer.current = setTimeout(() => {
                    setGetPropertiesLoading(false)
                }, 600)
            } else if (data.type === 'error') {
                toast.error(data.message || 'Error fetching Entrata properties')
                if (loadingDoneTimer.current) {
                    clearTimeout(loadingDoneTimer.current)
                }
                setGetPropertiesLoading(false)
            }
        }

        ws.onerror = () => {
            toast.error('Error connecting to WebSocket')
            setGetPropertiesLoading(false)
        }

        setSocket(ws)

        return () => {
            ws.close()
            if (loadingDoneTimer.current) {
                clearTimeout(loadingDoneTimer.current)
            }
        }
    }, [])

    const getOrgPropertiesFromEntrata = (selectedOrg: Organization) => {
        setGetPropertiesLoading(true)
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(
                JSON.stringify({
                    type: 'get_entrata_properties',
                    org_id: selectedOrg.id,
                }),
            )
            setOrgForCurrentEntrataProperties(selectedOrgId)
        } else {
            toast.error('WebSocket connection not available')
            setGetPropertiesLoading(false)
        }
    }

    return (
        <Container
            style={{
                flex: 1,
                padding: theme.spacing(2),
                flexDirection: 'column',
                overflow: 'scroll',
            }}
        >
            <span
                style={{
                    ...typographyStyle(theme.typography.h5),
                    fontWeight: 700,
                }}
            >
                Entrata Onboard
            </span>
            <Container
                style={{ alignItems: 'center', marginTop: theme.spacing(2) }}
            >
                <Selector
                    label="Organization"
                    data={organizationList ?? []}
                    currentValue={selectedOrgId}
                    onChange={(
                        event: React.ChangeEvent<{
                            value: unknown
                        }>,
                    ) => {
                        const orgId = event.target.value as number
                        setSelecetedOrgId(orgId)
                    }}
                    getDisplayString={(org) => org.name}
                    customStyle={{
                        formControl: selectorStyle,
                    }}
                    maxItems={100}
                    searchable
                />
                {/* Fixed-size container: swap button/spinner without changing layout */}
                <div
                    style={{
                        width: 125,
                        height: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {getPropertiesLoading ? (
                        <CircularProgress size={28} />
                    ) : (
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={selectedOrgId === -1}
                            style={{ ...buttonStyle, width: '100%' }}
                            onClick={() => {
                                const selectedOrg = organizationList?.find(
                                    (org) => org.id === selectedOrgId,
                                )
                                if (selectedOrg && selectedOrg.slug) {
                                    getOrgPropertiesFromEntrata(selectedOrg)
                                }
                            }}
                        >
                            Search
                        </Button>
                    )}
                </div>
            </Container>

            <Container
                style={{
                    alignItems: 'center',
                    marginTop: theme.spacing(2),
                }}
            >
                <Selector
                    label="Entrata Properties"
                    data={entrataProperties ?? []}
                    currentValue={selectedEntrataId}
                    onChange={(
                        event: React.ChangeEvent<{
                            value: unknown
                        }>,
                    ) => {
                        setSelectedEntrataId(event.target.value as number)
                    }}
                    getDisplayString={(property) => property.name}
                    customStyle={{
                        formControl: selectorStyle,
                    }}
                    maxItems={100}
                    searchable
                />
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={selectedEntrataId === -1 || getPropertiesLoading}
                    style={buttonStyle}
                    onClick={() => {
                        const selectedProperty = entrataProperties?.find(
                            (prop) => prop.id === selectedEntrataId,
                        )
                        if (selectedProperty) {
                            axiosInstance
                                .post(`company/entrata-onboard/`, {
                                    organization: orgForCurrentEntrataProperties,
                                    entrata_company_id: selectedProperty.id,
                                    name: selectedProperty.name,
                                    address: selectedProperty.address1,
                                    city: selectedProperty.city,
                                    state: selectedProperty.state ?? 'KY',
                                    zip: selectedProperty.zip_code,
                                })
                                .then(() => {
                                    toast.success(
                                        'You will recieve an email upon onboarding completion',
                                    )
                                })
                                .catch((e) => toast.error(e))
                        }
                    }}
                >
                    Onboard
                </Button>
            </Container>
            <div style={{ flex: 1 }} />
        </Container>
    )
}
